// Imports
import { Add, ArrowBack, MenuBookOutlined, Public } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { parseErrorForAxios } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { CouponModel, HubBikeDiscountType, HubDiscountModel } from '../utils/Models';
import { Colors, DefaultSliceData, DEFAULT_LOADER_STYLES } from '../../config/Constants';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { OfferTable } from './OfferTable';
import { OfferDetails } from './OfferDetails';
import { setNavSelection } from '../../features/navSelection';
import { GlobalOffers } from './GlobalOffers';

// Types and constants
type OfferDashboardView = 'all' | 'individual' | 'create' | 'global';
const defaultView: OfferDashboardView = 'all';
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  view: OfferDashboardView;
  offerData?: CouponModel;
}

// Main app component
export function OfferDashboard(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [localState, setLocalState] = useState<LocalState>({ view: defaultView });
  const [offerData, setOfferData] = useState<CouponModel[]>([]);

  // Local variables based on state
  const offerDetailsView = localState.view === 'create' || localState.view === 'global' || (localState.view === 'individual' && localState.offerData);

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const dispatch = useDispatch();

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(8));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load orders on first load
  const loadOffers = () => {
    const authCookie = getAuthCookie();
    if (!selectedHub?.id || !authCookie) return;
    setLocalState({ loading: true, message: '', view: defaultView });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/coupons`, { headers: { 'vendor-token': authCookie } })
        const offersList: CouponModel[] = data.data;
        setLocalState({ view: defaultView });
        setOfferData(offersList);
      } catch (e) {
        setLocalState({ error: true, message: parseErrorForAxios(e), view: defaultView });
      }
    })();
  }
  useEffect(loadOffers, [selectedHub]);

  // Utility function to activate or activate bike
  const setOfferActive = (offerId: number, activeState: boolean) => {
    const authCookie = getAuthCookie();
    const payload = { active: activeState };
    setLocalState({ loading: true, view: defaultView });
    (async () => {
      try {
        await GobikesApi.put(`/vendors/coupons/${offerId}`, payload, { headers: { 'vendor-token': authCookie } });
        setLocalState({ loading: false, view: defaultView })
        loadOffers();
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setLocalState({ error: true, message: errorMessage, loading: false, view: defaultView })
      }
    })();
  }

  // Loading view
  let mainContent = <div></div>;
  if (localState.loading) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (localState.error) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // All offers view
  else if (localState.view === 'all') {
    mainContent = <Fragment>
      <Box className='gbk-tabs' sx={{ typography: 'body1', m: '30px 0 0 0' }}>
        <Box className="phone-fab">
          <Button className="thick-border" disabled={Boolean(offerDetailsView)} onClick={e => setLocalState({ view: 'global' })} variant="outlined"><Public />&nbsp; Global</Button>&nbsp;
          <Button className="thick-border" disabled={Boolean(offerDetailsView)} onClick={e => setLocalState({ view: 'create' })} variant="outlined"><Add />New</Button>
        </Box>
        <OfferTable
          offerList={offerData}
          setParentState={setLocalState}
          containerClass={'gbk-grid-parent no-fabs'}
          setOfferActive={setOfferActive}
        />
      </Box >
    </Fragment>
  }

  // Individual offer view
  else if (localState.view === 'individual') {
    if (!localState.offerData) {
      setLocalState({ error: true, message: 'Invalid offer data', view: defaultView });
    } else {
      mainContent = (
        <OfferDetails
          offer={localState.offerData}
          backFn={(reload?: boolean) => {
            if (reload)
              return loadOffers();
            if (offerDetailsView)
              setLocalState({ view: defaultView, offerData: undefined })
          }}
          drawerWidth={props.drawerWidth}
          editMode={false}
          creating={false}
        />
      )
    }
  }

  // Create offer view
  else if (localState.view === 'create') {
    mainContent = (
      <OfferDetails
        offer={DefaultSliceData.DUMMY_OFFER}
        backFn={(reload?: boolean) => { if (reload) return loadOffers(); if (offerDetailsView) setLocalState({ view: defaultView, offerData: undefined }) }}
        drawerWidth={props.drawerWidth}
        editMode={true}
        creating={true}
      />
    )
  }

  // Create offer view
  else if (localState.view === 'global') {
    const globalDiscountData: HubDiscountModel = Object.assign({}, selectedHub.discount);
    if (globalDiscountData?.discountType === HubBikeDiscountType.PERCENT && globalDiscountData.value) {
      globalDiscountData.value = globalDiscountData.value * 100;
    }
    mainContent = (
      <GlobalOffers
        discount={globalDiscountData}
        backFn={(reload?: boolean) => { if (reload) return loadOffers(); if (offerDetailsView) setLocalState({ view: defaultView, offerData: undefined }) }}
        drawerWidth={props.drawerWidth}
      />
    )
  }

  // Default view
  else {
    mainContent = <div></div>;
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className={`gbk-tabcontent ${localState.view === 'all' ? '' : 'allow-scroll'}`}>

        {/* Global offers fab */}
        <Button
          onClick={e => setLocalState({ view: 'global' })}
          className='gbk-fab gbk-fab-top-right'
          sx={{ display: { xs: 'none', md: 'inline-flex' }, right: '240px !important' }}
          variant='contained'
          size='large'
          color='primary'
          disabled={Boolean(offerDetailsView)}
        >
          <Public /> &nbsp; Global Offers
        </Button>

        {/* Add offer fab */}
        <Button
          onClick={e => setLocalState({ view: 'create' })}
          className='gbk-fab gbk-fab-top-right'
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          variant='contained'
          size='large'
          color='primary'
          disabled={Boolean(offerDetailsView)}
        >
          <Add /> Create Offer
        </Button>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div
            className={`tab-header-fab ${offerDetailsView ? 'clickable' : ''}`}
            onClick={() => { if (offerDetailsView) setLocalState({ view: defaultView, offerData: undefined }) }} >
            {
              offerDetailsView
                ? <ArrowBack fontSize="large" />
                : <MenuBookOutlined fontSize="large" />
            }
          </div>

          {/* Text */}
          <Typography>
            <span style={{ ...(offerDetailsView && { color: Colors.primary }) }}>
              Offers
            </span>
            {offerDetailsView ? ' / Offer Details' : null}
          </Typography>
        </Box>

        {/* Main content */}
        {mainContent}

      </div >
    </Box >
  )

}